import React, {
	useEffect,
	useState,
	useMemo,
	useCallback,
	memo,
	Suspense,
	useLayoutEffect,
	useRef,
} from "react";
import { Column, Key, Option, OrderTabs, Tabs, useInternal } from "uikit";
import { cloneDeep, isEqual, uniqBy } from "lodash";
import styled from "styled-components";
import { LatLngLiteral } from "leaflet";
import { useDebouncedCallback } from "use-debounce/lib";
import { useTranslation } from "react-i18next";

import { useAppContext } from "../../../../../../context";
import { Order } from "../../../../../../services";
import createEmptyForm, {
	IOrderPoint,
	Schema,
	createOrderPointFromQuery,
	formatOrder,
	stringifyPoint,
	createOrderFormBy,
	prepareOrderByForm,
	formatOrderRoute,
	ThresholdPaymentStatus,
	// formatTaximeterOrderRoute,
} from "../../../../../../redux/constants/OrdersPage/order";
import { Orders } from "../../../../../../redux/reducers/Orders";
import {
	useTypedDispatch,
	useTypedSelector,
	store,
} from "../../../../../../redux/store";
import reveal from "../../../../../../redux/services/Map/getReveal";
import search from "../../../../../../redux/services/Map/search";
import calcRoute from "../../../../../../redux/services/Map/calcRoute";
import createEmptyTab from "../../../../../../redux/constants/OrdersPage/createEmptyTab";
import {
	openOrderCard,
	closeOrderCard,
	focusOrderCard,
} from "../../../../../../redux/services/Order/card";
import orderPage from "../../../../../../redux/reducers/OrdersPage";
import getOrderHistory, {
	History,
} from "../../../../../../redux/services/Order/getHistory";
import { getAndCallDown, callUp } from "../../../../../../utils/jsSip";
import createUIDMaker from "../../../../../../utils/createUIDMaker";
import { validateOrderForm } from "../../../../../../utils/validateOrderForm";
import {
	useCarParkFilterAccessControl,
	useCarParkIdsDecoder,
	useCheckFlagAccessControl,
	selectFilteredExecutorLocations,
} from "../../../../../../hooks";
import { useWidths } from "../../../../../../hooks/useTableSettings";
import OrderMap from "../../../../../../components/Map/Map";
import ModalController from "../../../../../../components/ModalController/Controller";
import PointModal from "../../../../../../components/PointModal";
import Modal from "../../../../../../components/Modal";
import tables from "../../../../../../constants/tables";
import { ColumnId } from "../../../../../../constants/tables/types";
import LegacyTableColumn from "../../../../../../types/TableColumn";
import {
	SuspenseLoader,
	StyledColumn,
	AccessGuard,
} from "../../../../../../components/common";
import MapExecutorLocations from "../../../../../../components/MapExecutorLocations";
import KeyBind from "../../../../../../components/KeyBind";
import {
	CompanyBillMetadata,
	BaseCompanyBillMetadata,
} from "../../../../../../components/Orders";
import { useNotification } from "../../../../../../components/NotificationProvider";
import { OrderModalProvider } from "../../../context";
import getPhoneInfo, {
	ValueClientPhoneType,
} from "../../../../../../redux/services/Order/getPhoneInfo";
import {
	BalanceHistory,
	createBalanceHistory,
	getAutoCreateKey,
	getNoAnswerForm,
	getYesAnswerForm,
	isNewBalanceOrder,
	rememberBalanceSuccess,
	shouldCheckBalance,
	skipBalanceTracking,
	syncBalanceWithServer,
	trackBalanceChanges,
} from "./balanceHistory";
import { useArchiveOrders } from "../../../Archives/hooks";
import { Parkings, Sectors, PriceZones } from "../../../components";
import InfoModalHistory from "../../../components/InfoModal/InfoModalHistory";
import OrderCalls from "../../../../../../components/OrderCalls";

import { TabAccessNames, TabKeys } from "./constants/access";
import { useGetSector } from "./hooks/useGetSector";
import {
	OrderCostRequest,
	OrderCostStage,
	useCalcOrderCost,
} from "./hooks/useCalcOrderCost";
import prepareValues from "./utils/prepareValues";
import { Tab } from "./types/Tab";
import OrderForm from "./OrderForm";
import FilesWidget from "./components/FilesWidget";
import MapController from "./components/MapController";
import ExecutorTrack from "./components/ExecutorTrack";
import { FooterMemo } from "./components/Footer";
import { ItemTabHeader } from "./components/ItemTabHeader";

const StyledWrap = styled.div`
	width: clamp(1302px, 80vw, 1402px);
	height: clamp(574px, 76vh, 768px);
	margin: auto;
	border-radius: 5px;
	background-color: #ffffff;
`;

const TabsWrap = styled(Tabs)`
	& > div:hover {
		color: #03a9f4;
	}
`;

const StyledWrapModal = styled(StyledColumn)`
	flex-wrap: nowrap;
	height: 100%;
	width: 100%;

	& > div:nth-child(1) {
		height: clamp(37px, 5.5vh, 47px);
		div .tabs {
			height: clamp(37px, 5.5vh, 47px);

			div.ui.attached.tabular.menu {
				padding: 5px 5px 0px 8px !important;
				height: clamp(32px, 4.3vh, 41px);

				div.tab {
					padding: 0px 14px 0px 14px !important;
					height: 100%;
				}
				div.addTabs {
					padding: 0px 0px 5px 14px !important;
					height: 100%;
					div {
						height: clamp(28px, 3.8vh, 32px);
						width: clamp(28px, 3.8vh, 32px);
						min-width: clamp(28px, 3.8vh, 32px);
					}
				}
			}
		}
	}
`;

const StyledWrapRight = styled(Column)`
	width: 100%;
	overflow: hidden;
	background: #ffffff;
`;

const StyledMapContainer = styled.div`
	position: relative;
	overflow: hidden;

	.map-tips-wrapper {
		position: absolute;
		padding: 10px 50px 5px 10px;
		z-index: 401;
		width: 100%;

		.map-tips-container {
			overflow: hidden;
			overflow-y: auto;
			max-height: calc(27.6px * 5);
			display: flex;
			flex-direction: column;
			border-radius: 3px;
			border: 1px solid #dee0e2;
			box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.05);

			::-webkit-scrollbar {
				width: 5px;
			}

			.map-tip-item {
				background: rgba(255, 255, 255, 0.9);
				color: #647079;
				padding: 4px;
				cursor: pointer;

				&.active {
					background: #ebebeb;
				}

				&:hover {
					background: #ebebeb;
				}

				:not(:last-child) {
					border-bottom: 1px solid #dee0e2;
				}
			}
		}
	}
`;

type ITab = "map" | "events" | "files" | "notes" | "conversations";

interface BaseTip {
	visible: boolean;
	data: IOrderPoint[];
	activeIndex: number;
}

interface Props {
	close: (ids: number[]) => void;
	activePoint: number;
	setActivePoint: (index: number) => void;
	pointModal: { visible: boolean; isInsert: boolean; isEdit: boolean };
	setPointModal: (data: {
		visible: boolean;
		isInsert: boolean;
		isEdit: boolean;
	}) => void;
}

/**
 * A taximeter order returned from the archive whose point changes on the
 * map takes the route and the price by the rate, as a change in the points
 * table already does (`OrderForm` clears the flag there; dev check F15).
 */
const dropTaximeterOnPointChange = (form: {
	processedByTaximeter?: boolean;
	revertedAt?: string | null;
}): { processedByTaximeter?: false } =>
	form?.processedByTaximeter && form?.revertedAt
		? { processedByTaximeter: false }
		: {};

const BaseOrderModal = ({
	close,
	activePoint,
	setActivePoint,
	pointModal,
	setPointModal,
}: Props) => {
	const baseTip: BaseTip = {
		visible: false,
		data: [],
		activeIndex: -1,
	};

	const dispatch = useTypedDispatch();
	const { t } = useTranslation();
	const { dateFns, countryTaxiService } = useAppContext();
	const { setActiveOrderId, setOrder } = useArchiveOrders();
	const { get: getSector } = useGetSector();
	const { get: getCalcOrderCost, getDiscountPlans } = useCalcOrderCost();
	const { isUserFleetOwner, accessFleetsIdsOnlyActive } =
		useCarParkFilterAccessControl<any>();
	const allCarParkIds = useCarParkIdsDecoder();
	const fleetIdsAll = useMemo(() => allCarParkIds(["all"]), [allCarParkIds]);
	const locationAccess = useMemo(
		() => ({
			isUserFleetOwner,
			accessFleetsIdsOnlyActive,
			fleetIdsAll,
		}),
		[accessFleetsIdsOnlyActive, fleetIdsAll, isUserFleetOwner],
	);
	const checkFlagAccessControl = useCheckFlagAccessControl(TabAccessNames);
	const { error } = useNotification();

	const translateTexts = useMemo(
		() => ({
			error: t("notification.title.error") ?? "Error",
			dataNotSave: t("notification.description.dataNotSave") ?? "Error",
			orderNumber:
				t("utils.getColumnsModeOne.orderNumber") || "Order number",
		}),
		[t],
	);
	const { user } = useTypedSelector((state) => state.account);
	const modal = useTypedSelector((state) => state.ordersPageReducer.modal);
	const mapCenter = useTypedSelector(
		(state) => state.ordersPageReducer.mapCenter,
	);
	const btnOnMap = useTypedSelector(
		(state) => state.orders.mapFromAddModal.toggles.btnOnMap,
	);

	const mapLanguage = useTypedSelector((state) => state.session.language);
	const settings = useTypedSelector((state) => state.settings.order.general);
	const { inputRequestDelayMs } = useTypedSelector(
		(state) => state.settings.map,
	);
	const polygons = useTypedSelector((state) => state.mapReducer.polygons);
	const { rates } = useTypedSelector(
		(state) => state.preferencesReducer.rates,
	);
	const { models: classes } = useTypedSelector(
		(state) => state.preferencesReducer.carClasses.data,
	);

	const [segmentsMap, setSegmentsMap] = useState<
		Map<number, { hash: string; segments: any[] }>
	>(new Map());

	// const [formMap, setFormMap] = useState<Map<number, Schema>>(new Map());

	// const [segmentsTaximeterMap, setSegmentsTaximeterMap] = useState<
	// 	Map<number, { hash: string; segments: any[] }>
	// >(new Map());

	const [allowMapFocusing, setPermissionBounds] = useState(true);
	const [acceptTapOpenModalEvent, setTabAccept] = useState(true);
	const [allowMapCentralization, setAllowMapCentralization] = useState(false);
	const [recalculate, setRecalculate] = useState<boolean>(false);
	const [isSendCalc, setIsSendCalc] = useState<boolean>(false);
	const [firstTabEvent, setTabEvent] = useState(false);
	const [cityTipsVisible, setCityTipsVisible] = useState(false);
	const [activeTabOrderId, setActiveTabOrderId] = useState<number>(-1);
	const [tabs, setTabs] = useState<Tab[]>([]);
	const [points, setPoints] = useState<IOrderPoint[]>([]);
	const [pointsTableKey, setPointsTableKey] = useState(0);
	const distance = 0;
	const [revealPoints, setRevealPoints] = useState<any>([]);
	const [mapRevealTipsState, setMapRevealTipsState] =
		useState<BaseTip>(baseTip);
	const [centroid, setCentroid] = useState<number[] | LatLngLiteral>(
		user?.defaultTaxiService?.settlementPoint,
	);
	const [allTips, setAllTips] = useState<IOrderPoint[]>([]);
	const [point, setPoint] = useState<{ [key: string]: any }>({
		value: "",
		address: { value: "" },
		city: user?.defaultTaxiService,
	});
	const [loadingBtnSubmit, setLoadingBtnSubmit] = useState<boolean>(false);
	const [loadingChangePoint, setLoadingChangePoint] =
		useState<boolean>(false);
	/** to blurring the cost of an item when changing points or getting a price */
	const [loadingChangeCost, setLoadingChangeCost] = useState<boolean>(false);

	const [selectedRightTab, setSelectedRightTab] = useState<Key>("map");
	/** Calls subscribe on the first visit of their tab and stay alive after. */
	const [callsTabVisited, setCallsTabVisited] = useState(false);
	const [history, setHistory] = useState<History[]>([]);
	const [displayBadges, setDisplayBadges] =
		useInternal<Orders.MapFromAddModal.BtnOnMapToggles>(btnOnMap);
	const [companyBillMetadataModal, setCompanyBillMetadataModal] =
		useInternal<boolean>(false);
	/** One record per order tab, see `balanceHistory.ts`. */
	const balanceHistoryRef = useRef(new Map<number, BalanceHistory>());
	/** The orders whose calculation has come back and is not looked at yet. */
	const calculatedOrderIdsRef = useRef(new Set<number>());
	/** The phone type asked for the dialog: a saved order does not know it. */
	const [balancePhoneType, setBalancePhoneType] = useState<{
		phone: string;
		type?: ValueClientPhoneType;
		loaded: boolean;
	} | null>(null);

	const nextUID = useMemo(() => createUIDMaker(), []);

	const getExecutorLocations = useCallback(() => {
		if (selectedRightTab !== "map") return [];
		return selectFilteredExecutorLocations(
			store.getState(),
			{},
			locationAccess,
		);
	}, [locationAccess, selectedRightTab]);

	// Data from modal.data.cards
	useEffect(() => {
		if (!modal.open) return;
		if (modal.data.activeCardId) {
			const updateTabs = modal.data.cards?.map((card) =>
				createTab(card.order, card.id),
			);
			// These forms replace the open ones below. It is not a dispatcher's action.
			updateTabs?.forEach((item) => {
				const history = balanceHistoryRef.current.get(
					item.form.id as number,
				);
				if (history && item.form.status !== "creating") {
					syncBalanceWithServer(history, item.form);
				}
			});
			// TODO: need to rewrite it
			setTabs((prev) => {
				if (!prev.length) return updateTabs;

				const items = updateTabs.map((item) => {
					if (item.form.status !== "creating") return item;

					const exist = prev.find(
						(prevItem) =>
							item.cardId != null &&
							prevItem.cardId === item.cardId,
					);

					if (exist) return exist;
					return item;
				});

				return items;
			});

			const id = modal.data.cards.find(
				(card) => card.id === modal.data.activeCardId,
			)?.order?.id;

			setActiveTabOrderId(id);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user, modal, modal.data.cards, modal.data.activeCardId, dispatch]);

	const activeCard = useMemo(() => {
		if (modal.data.cards.length) {
			const card = modal.data.cards.find(
				(item) => item.order.id === modal.data.activeCardId,
			);

			return {
				...card,
				order: formatOrder(card?.order || {}),
			};
		}

		return null;
	}, [modal.data]);

	const trackOrderId: number | undefined = activeCard?.order?.id;
	const trackExecutorId: number | undefined =
		activeCard?.order?.executorToOrder?.[0]?.executor?.id;
	/** The executor track is loaded once per enabling, not in real time */
	const [showTrack, setShowTrack] = useState(false);

	useEffect(() => {
		setShowTrack(false);
	}, [trackOrderId, trackExecutorId]);

	const OPTIONS: Option<ITab>[] = useMemo(() => {
		const optionsTab: Option<ITab>[] = [];

		if (checkFlagAccessControl[TabKeys.MAP]) {
			optionsTab.push({
				key: "map",
				label: "mainPage.archives.orders.modal.tabs.map",
				value: "map",
			});
		}
		if (checkFlagAccessControl[TabKeys.EVENTS]) {
			optionsTab.push({
				key: "events",
				label: "mainPage.archives.orders.modal.tabs.events",
				value: "events",
			});
		}
		if (checkFlagAccessControl[TabKeys.CONVERSATIONS]) {
			optionsTab.push({
				key: "conversations",
				label: "mainPage.archives.orders.modal.tabs.conversations",
				value: "conversations",
			});
		}

		return optionsTab;
	}, [checkFlagAccessControl]);

	const saveRevealPoint = useCallback(
		(data) => {
			const revealPointsCopy = [...revealPoints];
			const revealPointIndex = revealPointsCopy.findIndex(
				(item) => item.index === data.index,
			);
			if (revealPointIndex !== -1) {
				revealPointsCopy[revealPointIndex] = data;
				setRevealPoints(revealPointsCopy);
			} else {
				setRevealPoints([...revealPointsCopy, data]);
			}

			return data;
		},
		[revealPoints],
	);

	const defaultFormValues = useCallback<() => Schema>(() => {
		const carClass = classes.find((item) => item.default);
		const emptyForm = createEmptyForm();

		if (user) {
			emptyForm.companyID = user.defaultTaxiService?.company?.id;
			emptyForm.taxiServiceId = user.defaultTaxiService?.id;

			const language = user.settings?.language || "en";

			if (carClass) {
				emptyForm.taxiClass = [
					{ name: carClass.name?.[language], value: carClass.id },
				];
			}
		}

		if (rates.length && carClass) {
			const carClassIds = emptyForm.taxiClass
				? emptyForm.taxiClass.map((item) => item.value)
				: [carClass.id];

			const defaultRate = rates.filter((rate) => {
				if (!rate?.active) return false;
				const cars = carClassIds
					.map((item) => rate.carClassIds.includes(item))
					.every((item) => item);

				const taxi = rate.taxiServiceIds.includes(
					emptyForm.taxiServiceId,
				);

				const company = rate.taxiServices.find((service) => {
					if (service.company?.id === emptyForm.companyID)
						return true;
					return false;
				});

				if (taxi && company && cars) return true;
				return false;
			});

			if (defaultRate.length) {
				const rate =
					defaultRate.find((rate) => {
						if (rate.root) return false;
						if (rate.default) return false;
						if (
							rate.taxiServiceIds.length === 1 &&
							rate.carClassIds.length === 1
						)
							return true;
						return false;
					}) ||
					defaultRate.find((rate) => {
						if (rate.root) return false;
						if (rate.default) return false;
						if (
							rate.taxiServiceIds.length >= 1 &&
							rate.carClassIds.length === 1
						)
							return true;
						return false;
					}) ||
					defaultRate.find((rate) => {
						if (rate.root) return false;
						if (rate.default) return false;
						if (
							rate.taxiServiceIds.length === 1 &&
							rate.carClassIds.length >= 1
						)
							return true;
						return false;
					}) ||
					defaultRate.find((rate) => {
						if (rate.root) return false;
						if (rate.default) return false;
						if (
							rate.taxiServiceIds.length >= 1 &&
							rate.carClassIds.length >= 1
						)
							return true;
						return false;
					}) ||
					defaultRate.find((rate) => {
						if (rate.root) return false;
						if (rate.default) return true;
						return false;
					}) ||
					defaultRate.find((rate) => {
						if (rate.root) return true;
						return false;
					}) ||
					defaultRate.at?.(0);
				const rateSettings = rate.additionalFields;
				const { autoCalculatePickupCost, autoCalculateRoundTrip } =
					rateSettings.general;
				emptyForm.rateId = rate.id;
				emptyForm.rateIncludePickupCost = autoCalculatePickupCost;
				emptyForm.autoCalculateRoundTrip = autoCalculateRoundTrip;
			}
		}

		return {
			...emptyForm,
			createdAt: Date.now(),
		};
	}, [rates, classes, user]);

	const setCenter = useCallback(
		(newCenter) => {
			dispatch(orderPage.actions.setMapCenter(newCenter));
		},
		[dispatch],
	);

	const createTab = useCallback<(order?: any, cardId?: number) => Tab>(
		(order, cardId): Tab => {
			const orderValue = order ?? {};

			const schema = orderValue?.id
				? createOrderFormBy(orderValue, user?.settings?.language)
				: {
						status: undefined,
						rateId: 1,
				  };

			const defaultValues = defaultFormValues();
			const rateId =
				schema.status === undefined || schema.status === "creating"
					? defaultValues.rateId
					: schema.rateId;

			const plans = getDiscountPlans(
				orderValue?.cost?.settings?.customerDiscountPlans,
			);

			const form: Schema = {
				...defaultValues,
				...schema,
				additionalPlan: plans?.additionalPlan,
				mainPlan: plans?.mainPlan,
				rateId,
			};

			if (orderValue?.id && orderValue.orderDate) {
				form.orderDate = form.orderDate
					? dateFns.toZonedTime(form.orderDate)
					: form.orderDate;
				if (orderValue?.isPreliminary) {
					form.scheduledTime = form.scheduledTime
						? dateFns.toZonedTime(form.scheduledTime).getTime()
						: form.scheduledTime;
				}
			}

			// const routeTaximeter = formatTaximeterOrderRoute(order);
			const route = formatOrderRoute(orderValue?.route) || null;
			const uid = nextUID();
			const data = createEmptyTab();

			const validator: any = Object.keys(form).reduce((acc, key) => {
				acc[key] = true;
				return acc;
			}, {});

			const taxiService =
				orderValue?.taxiService || user?.defaultTaxiService;
			const userLanguage = user?.settings?.language || "en";
			const passenger = orderValue?.passengers?.at(0) || [];
			if (!orderValue?.id) setCenter(taxiService.settlementPoint);

			// console.log("[Order] createTab", { orderValue, form });

			return {
				id: form.id,
				cardId: cardId ?? form.id,
				uid,
				data,
				form,
				validator,
				state: {
					customerId: passenger ? passenger?.customer?.id : null,
					defaultCity: {
						id: taxiService.id,
						value: taxiService.settlement[userLanguage],
						settlement: taxiService.settlement[userLanguage],
						coordinates: taxiService.settlementPoint,
					},
					popularAddresses: [],
					additionalNumbers: Math.max(
						1,
						form?.additionalPhones?.length || 0,
					),
					modals: {
						time: false,
						classes: false,
						services: false,
					},
					route,
					routeTaximeter: null,
				},
				setState(state) {
					setTabs((prev) => {
						// TODO: need to rewrite it
						const newTabs = [...prev];
						const index = newTabs.findIndex(
							(tab) => tab.uid === uid,
						);

						if (index !== -1) {
							newTabs[index] = {
								...newTabs[index],
								state:
									typeof state === "function"
										? state(newTabs[index].state)
										: state,
							};

							const coordinates =
								newTabs[index]?.form?.points?.at(
									-1,
								)?.coordinates;
							if (coordinates?.lat || coordinates?.lng) {
								setCenter(coordinates);
							}

							return newTabs;
						}

						return prev;
					});
				},
			};
		},

		[
			user?.settings?.language,
			user?.defaultTaxiService,
			defaultFormValues,
			getDiscountPlans,
			nextUID,
			setCenter,
			dateFns,
		],
	);

	const activeTabIndex = useMemo(
		() => tabs.findIndex((card) => card?.form?.id === activeTabOrderId),
		[activeTabOrderId, tabs],
	);

	const orderFormTab: Tab = useMemo<Tab>(
		() => {
			const value =
				tabs.find((order) => order?.id === activeTabOrderId) ||
				createTab();
			return value;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			// eslint-disable-next-line react-hooks/exhaustive-deps
			activeTabOrderId,
			activeTabIndex,
			// eslint-disable-next-line react-hooks/exhaustive-deps
			tabs[activeTabIndex]?.form,
			// eslint-disable-next-line react-hooks/exhaustive-deps
			tabs[activeTabIndex]?.state,
			// eslint-disable-next-line react-hooks/exhaustive-deps
			tabs[activeTabIndex]?.validator,
		],
	);

	const isNotViewBecauseOfAccess = useMemo(() => {
		const isNotAccessViewInModalOrder =
			!checkFlagAccessControl[TabKeys.VIEW];

		return isNotAccessViewInModalOrder;
	}, [checkFlagAccessControl]);

	const isDisabled = useMemo(() => {
		const isOwn = orderFormTab?.form?.isOwn || false;

		const status = orderFormTab.form?.status === "closed";

		const isNotAccessEditInModalOrder =
			!checkFlagAccessControl[TabKeys.EDIT];

		if (status || isNotAccessEditInModalOrder) return true;
		if (!isOwn) {
			const hasExecutor = orderFormTab?.form?.executorSip;
			return !hasExecutor;
		}
		return Boolean(activeCard?.lockedBy);
	}, [
		orderFormTab.form?.isOwn,
		orderFormTab.form?.status,
		orderFormTab.form?.executorSip,
		checkFlagAccessControl,
		activeCard?.lockedBy,
	]);

	const handleCloseActiveCard = useCallback(() => {
		setMapRevealTipsState(baseTip);
		if (activeCard.id) {
			close([activeCard.id]);
			segmentsMap.delete(activeCard.id);
			// segmentsTaximeterMap.delete(activeCard.id);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeCard, close]);

	const handleCloseModal = useCallback(() => {
		setMapRevealTipsState(baseTip);
		const cardIds = modal.data.cards.map((card) => card.id);
		cardIds.forEach((card) => {
			segmentsMap.delete(card.id);
			// segmentsTaximeterMap.delete(card.id);
		});
		close(cardIds);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [close, modal.data.cards]);

	const prepareFormData = useCallback(() => {
		const values = prepareValues(orderFormTab.form);
		const { id } = orderFormTab.form;
		const { customerId } = orderFormTab.state;

		const data = prepareOrderByForm(
			values,
			customerId || activeCard.order?.passengers?.[0]?.customer?.id,
		);

		return { id, params: data };
	}, [activeCard, orderFormTab]);

	const getBalanceHistory = useCallback((orderId: number) => {
		const map = balanceHistoryRef.current;
		if (!map.has(orderId)) map.set(orderId, createBalanceHistory());
		return map.get(orderId) as BalanceHistory;
	}, []);

	const orderFormRef = useRef(orderFormTab.form);
	orderFormRef.current = orderFormTab.form;

	/**
	 * The fast check before an order is saved. `false`: the money is short,
	 * the dialog opens and the order must not be saved now.
	 */
	const ensureBalance = useCallback(async (): Promise<boolean> => {
		const form = orderFormRef.current;
		const history = form?.id ? getBalanceHistory(form.id) : null;
		if (!form || !shouldCheckBalance(form, history?.baseline ?? null)) {
			return true;
		}

		const { params } = prepareFormData();
		const metadata = await Order.checkBalance({
			orderId: form.id,
			checkCardId: form.counterparty?.checkCardId as number,
			counterpartyId: form.counterparty?.id as number,
			amount: form.price ?? 0,
			orderDate: params.orderDate,
			isPreliminary: params.isPreliminary,
			taxiServiceId: params.taxiServiceId,
		});

		// The price changed while the check was running: this answer is old.
		if (orderFormRef.current?.price !== form.price) return ensureBalance();

		if (metadata?.status !== ThresholdPaymentStatus.THRESHOLD_FAILED) {
			return true;
		}

		setTabs((items) =>
			items.map((tab) =>
				tab.form?.id === form.id
					? {
							...tab,
							form: {
								...tab.form,
								companyBillMetadata: metadata,
							},
					  }
					: tab,
			),
		);
		return false;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getBalanceHistory, prepareFormData]);

	/** The order number tells the balance dialog that this order is not a draft any more. */
	const onOrderCreated = useCallback((order: Order.Model) => {
		setTabs((items) =>
			items.map((tab) =>
				tab.form?.id === order.id
					? {
							...tab,
							form: {
								...tab.form,
								orderNumber: order.orderNumber,
							},
					  }
					: tab,
			),
		);
	}, []);

	const silentSubmit = useCallback(() => {
		if (!tabs.length) return;

		const createOrderOption = settings.requiredFields;
		const validator = validateOrderForm(orderFormTab.form, {
			phone: createOrderOption.phone,
			points: createOrderOption.points,
			country: countryTaxiService,
		});
		const valid = Object.values(validator).every(Boolean);

		if (valid) {
			const preparedData = prepareFormData();

			Order.update({ id: preparedData.id, ...preparedData.params }).catch(
				(error) => {
					console.error("[OrderModal]: update order error:", error);
				},
			);
		}
	}, [
		tabs.length,
		settings.requiredFields,
		orderFormTab.form,
		prepareFormData,
		countryTaxiService,
	]);

	// --- Auto update order ---
	const handleOrderUpdate = useCallback(async () => {
		if (activeCard?.lockedBy) return;
		if (!modal.data.activeCardId) return;

		if (!(await ensureBalance())) return;

		const formOrder = orderFormTab.form;

		const { customerId } = orderFormTab.state;

		const payload: Order.Model.Modified = prepareOrderByForm(
			formOrder,
			customerId || activeCard.order?.passengers?.[0]?.customer?.id,
		);

		if (formOrder.id) {
			await Order.update(
				{ ...payload, id: formOrder.id },
				{ dispatcherModal: true },
			);
			// This price and counterparty are saved now.
			getBalanceHistory(formOrder.id).baseline = {
				price: formOrder.price,
				checkCardId: formOrder.counterparty?.checkCardId,
			};
		}
	}, [
		activeCard?.lockedBy,
		activeCard.order?.passengers,
		modal.data.activeCardId,
		orderFormTab.form,
		orderFormTab.state,
		ensureBalance,
		getBalanceHistory,
	]);

	const debounceOrderUpdateField = useDebouncedCallback(
		(save = false) => {
			if (!settings.enableQuickDistribution) return;
			if (!settings.autosaveOrderSettings.active || !save) return;
			if (orderFormTab?.form.orderPointSave) return;

			handleOrderUpdate();
		},
		settings.autosaveOrderSettings.active
			? settings.autosaveOrderSettings?.delayAfterUpdateField || 0
			: 0,
	);

	const delayAfterRevealPoint = useMemo(() => {
		const delay = settings.autosaveOrderSettings?.delayAfterRevealPoint;
		if (delay) return delay + 1000;
		return 1000;
	}, [settings.autosaveOrderSettings?.delayAfterRevealPoint]);

	const debounceOrderUpdateRevealPoint = useDebouncedCallback(
		(save = false) => {
			if (!settings.enableQuickDistribution) return;
			if (!settings.autosaveOrderSettings.active || !save) return;
			if (orderFormTab?.form.orderSave) return;

			handleOrderUpdate();
		},
		delayAfterRevealPoint,
	);

	const delayAutosaveOrderSettings = useMemo(() => {
		if (settings.autosaveOrderSettings.active) {
			const delay =
				settings.autosaveOrderSettings.delayAfterSearchAddress;
			if (delay) return delay + 200;
			return 200;
		}
		return 0;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [settings.autosaveOrderSettings.delayAfterSearchAddress]);

	const debounceOrderUpdatePoint = useDebouncedCallback((save = false) => {
		if (!settings.enableQuickDistribution) return;
		if (!settings.autosaveOrderSettings.active || !save) return;
		if (orderFormTab?.form.orderSave) return;

		handleOrderUpdate();
	}, delayAutosaveOrderSettings);

	const onSavePoints = useCallback(() => {
		if (orderFormTab?.form?.status) {
			const options = ["closed", "creating"];
			const status = orderFormTab.form.status || "closed";
			if (status !== "closed") setRecalculate(true);
			if (options.includes(status)) return null;
			setLoadingChangeCost(true);
			debounceOrderUpdatePoint(true);
		}
		return null;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		orderFormTab?.form?.status,
		orderFormTab?.form?.points,
		activeCard,
		modal,
	]);

	useEffect(() => {
		if (orderFormTab?.form.orderPointSave && orderFormTab?.form?.status) {
			const options = ["closed", "creating"];
			const status = orderFormTab?.form.status || "closed";
			if (options.includes(status)) return;
			debounceOrderUpdatePoint(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		orderFormTab?.form.orderPointSave,
		orderFormTab?.form.status,
		orderFormTab?.form.points,
	]);

	useEffect(() => {
		if (orderFormTab?.form.orderSave && orderFormTab?.form.status) {
			const options = ["closed", "creating"];
			const status = orderFormTab?.form.status || "closed";
			if (options.includes(status)) return;
			debounceOrderUpdateField(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orderFormTab?.form.orderSave, orderFormTab?.form.status]);

	// --- end ---
	const handleSubmit = useCallback(async () => {
		if (loadingBtnSubmit) return;

		setLoadingBtnSubmit(true);

		const createOrderOption = settings.requiredFields;
		const orderNumber = orderFormTab?.form?.orderNumber || "";
		const validator = validateOrderForm(orderFormTab.form, {
			phone: createOrderOption.phone,
			points: createOrderOption.points,
			country: countryTaxiService,
		});

		const valid = Object.values(validator).every(Boolean);

		if (valid) {
			// Before the card closes: when the money is short the window stays open.
			if (!(await ensureBalance())) {
				setLoadingBtnSubmit(false);
				return;
			}

			const { id, params } = prepareFormData();

			if (id) dispatch(closeOrderCard(id));

			const res = Order.update(
				{ id, ...params },
				{ dispatcherModal: true },
			);
			setLoadingBtnSubmit(false);
			const response = await res;

			if (!response?.id) {
				error(
					`${translateTexts.error} ${translateTexts.orderNumber}: ${orderNumber}`,
					translateTexts.dataNotSave,
					true,
				);
			}

			if (response && response?.id) {
				dispatch(
					orderPage.actions.setActiveOrder(formatOrder(response)),
				);

				if (!id) dispatch(closeOrderCard(response.id));
				segmentsMap.delete(response.id);
			}

			setLoadingBtnSubmit(false);
		} else {
			setTabs((prev) =>
				prev.map((tab) => {
					if (tab.form?.id !== activeTabOrderId) return tab;
					return { ...tab, validator };
				}),
			);
			setLoadingBtnSubmit(false);
		}
		setLoadingBtnSubmit(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tabs, activeTabOrderId, prepareFormData, dispatch, ensureBalance]);

	const handleClosePointModal = useCallback(() => {
		setPointModal({ visible: false, isInsert: false, isEdit: false });
		setTabAccept(false);
		setTabEvent(false);
		setMapRevealTipsState(baseTip);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [setPointModal]);

	const handleAddTab = useCallback(async () => {
		// Only a valid form is saved by `silentSubmit`: nothing to check otherwise.
		const createOrderOption = settings.requiredFields;
		const validator = validateOrderForm(orderFormTab.form, {
			phone: createOrderOption.phone,
			points: createOrderOption.points,
			country: countryTaxiService,
		});
		if (Object.values(validator).every(Boolean)) {
			if (!(await ensureBalance())) return;
		}

		silentSubmit();
		setMapRevealTipsState(baseTip);
		dispatch(openOrderCard());
		dispatch(orderPage.actions.setBufferOpenNewTab(true));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, silentSubmit, ensureBalance, orderFormTab.form]);

	const handleChangeTab = useCallback(
		(index: number) => {
			if (index === activeTabIndex) return;
			const orderId = modal.data.cards?.[index]?.id;
			if (orderId) {
				setSegmentsMap((prev) => {
					const exist = prev.get(orderId);
					if (exist) {
						prev.set(orderId, {
							hash: "reset",
							segments: exist.segments,
						});
					}
					return prev;
				});

				// setSegmentsTaximeterMap((prev) => {
				// 	const exist = prev.get(orderId);
				// 	if (exist) {
				// 		prev.set(orderId, {
				// 			hash: "reset",
				// 			segments: exist.segments,
				// 		});
				// 	}
				// 	return prev;
				// });
				setActiveTabOrderId(orderId);
			}

			if (orderId) dispatch(focusOrderCard(orderId));
		},
		[activeTabIndex, modal?.data?.cards, dispatch],
	);

	const handleCloseTab = useCallback(
		(index) => {
			if (typeof index !== "number") return;
			if (modal.data.cards?.[index]?.id) {
				const orderId = modal.data.cards?.[index]?.id;
				dispatch(closeOrderCard(orderId));
				segmentsMap.delete(orderId);
				// segmentsTaximeterMap.delete(orderId);
			}
			setMapRevealTipsState(baseTip);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[dispatch, modal.data.cards],
	);

	const handleCloseOrderAndTab = useCallback(
		(orderId: number) => {
			if (orderId) {
				setMapRevealTipsState(baseTip);
				close([orderId]);
				segmentsMap.delete(orderId);
				// segmentsTaximeterMap.delete(orderId);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[orderPage, closeOrderCard, dispatch, close, setMapRevealTipsState],
	);

	const handleChangePoint = useCallback(
		async (index, coordinates, response) => {
			setLoadingChangePoint(true);
			setLoadingChangeCost(true);
			try {
				if (activeCard?.lockedBy) return null;
				setActivePoint(index);

				if (!response?.objects?.length) return null;

				const pointOnMap = t(["point_on_map"]);

				const convertedObjects: any = uniqBy(
					response.objects.map((object) =>
						createOrderPointFromQuery(object, mapLanguage),
					),
					(item: any) => stringifyPoint(item, "full", pointOnMap),
				);

				const firstAddressIndex = convertedObjects.findIndex(
					(item) => item.type === "address",
				);
				const activeIndex =
					firstAddressIndex === -1 ? 0 : firstAddressIndex;

				const object = convertedObjects[activeIndex];
				const taxiServiceId = orderFormTab?.form?.taxiServiceId;

				const addSector = async () => {
					if (
						(object.sector === null && coordinates) ||
						(object.sector === undefined && coordinates)
					) {
						const sector = await getSector(coordinates, {
							taxiServiceId,
						});
						if (sector) {
							return { id: sector?.id, name: sector?.name };
						}
					}
					if (
						(object.sector === null && object?.coordinates) ||
						(object.sector === undefined && object?.coordinates)
					) {
						const sector = await getSector(object.coordinates, {
							taxiServiceId,
						});
						if (sector) {
							return { id: sector?.id, name: sector?.name };
						}
					}

					return null;
				};
				const sector = await addSector();
				const newOject = sector ? { ...object, sector } : object;
				const tipsObjects = convertedObjects?.map((item) => {
					if (isEqual(item.coordinates, newOject.coordinates))
						return newOject;
					return item;
				});

				saveRevealPoint({
					index,
					tips: tipsObjects,
					activeIndex,
				});
				setMapRevealTipsState({
					visible: true,
					data: tipsObjects,
					activeIndex,
				});

				setRecalculate(true);
				setTabs((tabs) => {
					const newTabs = [...tabs];
					return newTabs.map((tab) => {
						if (tab?.form?.id === activeTabOrderId) {
							const newPoints = [...tab.form.points];
							newPoints[index] = {
								...newOject,
								id: index + 1,
								customStreet:
									`${newOject.streetType} ${newOject.street}`.trim(),
								customHouse: `${newOject.house}`,
								coordinates,
							};
							const item = {
								...tab,
								form: {
									...tab.form,
									...dropTaximeterOnPointChange(tab.form),
									points: newPoints,
									orderPointSave: false,
									orderSave: false,
								},
							};
							return item;
						}

						return tab;
					});
				});

				if (orderFormTab.form?.status) {
					const options = ["closed", "creating"];
					const status = orderFormTab?.form.status || "closed";
					if (options.includes(status)) return null;
					debounceOrderUpdateRevealPoint(true);
				}

				return null;
			} finally {
				setLoadingChangePoint(false);
			}
		},

		[
			activeCard?.lockedBy,
			setActivePoint,
			t,
			saveRevealPoint,
			orderFormTab?.form?.status,
			mapLanguage,
			getSector,
			activeTabOrderId,
			debounceOrderUpdateRevealPoint,
			orderFormTab?.form?.taxiServiceId,
		],
	);

	const pointsLength = useMemo(
		() => orderFormTab?.form?.points?.length || 0,
		[orderFormTab?.form?.points?.length],
	);

	const handleAddPoint = useCallback(async () => {
		if (activeCard?.lockedBy) return;

		const pointOnMap = t(["point_on_map"]);
		setLoadingChangePoint(true);
		setLoadingChangeCost(true);

		try {
			const taxiServiceId = orderFormTab?.form?.taxiServiceId;
			const response = await reveal(mapCenter, { taxiServiceId });
			if (!response?.objects?.length) return;

			const convertedObjects: any = uniqBy(
				response.objects.map((object) =>
					createOrderPointFromQuery(object, mapLanguage),
				),
				(item: any) => stringifyPoint(item, "full", pointOnMap),
			);

			const firstAddressIndex = convertedObjects.findIndex(
				(item) => item.type === "address",
			);
			const activeIndex =
				firstAddressIndex === -1 ? 0 : firstAddressIndex;

			const object = convertedObjects[activeIndex];

			const sector = await (async () => {
				if (
					(object.sector === null && object?.coordinates) ||
					(object.sector === undefined && object?.coordinates)
				) {
					const s = await getSector(object.coordinates, {
						taxiServiceId,
					});
					if (s) return { id: s.id, name: s.name };
				}
				return null;
			})();

			const newOject = sector ? { ...object, sector } : object;

			const tipsObjects = convertedObjects.map((item) =>
				isEqual(item.coordinates, newOject.coordinates)
					? newOject
					: item,
			);

			saveRevealPoint({
				index: points.length,
				tips: tipsObjects,
				activeIndex,
			});
			setMapRevealTipsState({
				visible: true,
				data: tipsObjects,
				activeIndex,
			});

			setRecalculate(true);

			const getTaximeterStatus = () => {
				if (pointsLength === 0 && settings.singlePointMetering)
					return true;
				if (pointsLength > 0 && settings.singlePointMetering)
					return false;
				if (settings.allOrdersMetering) return true;
				return null;
			};

			const isTaximeter = getTaximeterStatus();

			setTabs((tabs) =>
				tabs.map((tab) => {
					if (tab.form?.id !== activeTabOrderId) return tab;

					return {
						...tab,
						form: {
							...tab.form,
							...dropTaximeterOnPointChange(tab.form),
							orderPointSave: false,
							orderSave: false,
							taximeter:
								isTaximeter !== null
									? isTaximeter
									: tab.form.taximeter,
							points: [
								...tab.form.points,
								{
									...newOject,
									id: tab.form.points.length + 1,
									customStreet:
										`${newOject.streetType} ${newOject.street}`.trim(),
									customHouse: `${newOject.house}`,
									coordinates: mapCenter,
								},
							],
						},
					};
				}),
			);
			setActivePoint(points.length);

			if (orderFormTab.form?.status) {
				const options = ["closed", "creating"];
				const status = orderFormTab.form.status || "closed";
				if (options.includes(status)) return;
				debounceOrderUpdateRevealPoint(true);
			}
		} finally {
			setLoadingChangePoint(false);
		}
	}, [
		activeCard?.lockedBy,
		t,
		mapCenter,
		pointsLength,
		settings.singlePointMetering,
		settings.allOrdersMetering,
		saveRevealPoint,
		points.length,
		setActivePoint,
		orderFormTab.form.status,
		mapLanguage,
		getSector,
		activeTabOrderId,
		debounceOrderUpdateRevealPoint,
		orderFormTab?.form?.taxiServiceId,
	]);

	const selectRevealTip = useCallback(
		(object, index) => {
			setLoadingChangeCost(true);
			setMapRevealTipsState({
				...mapRevealTipsState,
				activeIndex: index,
			});

			setRecalculate(true);
			setTabs((tabs) => {
				const newTabs = [...tabs];
				return newTabs.map((tab) => {
					if (tab?.form?.id === activeTabOrderId) {
						const newPoints = [...tab.form.points];
						newPoints[activePoint] = { ...object };
						const item = {
							...tab,
							form: {
								...tab.form,
								...dropTaximeterOnPointChange(tab.form),
								points: newPoints,
								orderPointSave: false,
								orderSave: false,
							},
						};
						return item;
					}
					return tab;
				});
			});

			if (orderFormTab.form?.status) {
				const options = ["closed", "creating"];
				const status = orderFormTab.form.status || "closed";
				if (options.includes(status)) return null;
				debounceOrderUpdateRevealPoint(true);
			}
			return null;
		},

		[
			mapRevealTipsState,
			orderFormTab.form?.status,
			activeTabOrderId,
			activePoint,
			debounceOrderUpdateRevealPoint,
		],
	);

	useEffect(() => {
		if (loadingChangePoint) {
			setLoadingChangeCost(true);
		}
	}, [loadingChangePoint]);

	useEffect(() => {
		const revealPointTips = revealPoints.find(
			(item) => +item.index === +activePoint,
		);

		if (revealPointTips) {
			setMapRevealTipsState({
				visible: true,
				data: revealPointTips.tips,
				activeIndex: revealPointTips.activeIndex,
			});
		} else {
			setMapRevealTipsState(baseTip);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [revealPoints, activePoint]);

	useEffect(() => {
		setCentroid(
			activeCard?.order?.taxiService?.settlementPoint ||
				user?.defaultTaxiService?.settlementPoint,
		);
	}, [activeCard, user?.defaultTaxiService, user]);

	// centroid map by point
	useEffect(() => {
		if (!orderFormTab?.state?.defaultCity?.coordinates) return;
		if (!isEqual(centroid, orderFormTab?.state?.defaultCity?.coordinates)) {
			setCentroid(
				orderFormTab?.state?.defaultCity?.coordinates ||
					user?.defaultTaxiService.settlementPoint,
			);
			setAllowMapCentralization(true);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orderFormTab?.state?.defaultCity?.coordinates]);

	useEffect(() => {
		let cancel = false;
		const normalacyArr = [...points].filter((item) => {
			if (!item) return false;
			return true;
		});

		// TODO: Unnecessarily requests when opening an existing order
		if (
			normalacyArr.length > 1 &&
			!orderFormTab.form.processedByTaximeter
		) {
			const points = normalacyArr
				.filter((item) => {
					if (item.coordinates?.lat === 0) return false;
					if (item.coordinates?.lng === 0) return false;
					return true;
				})
				.map((item) => item?.coordinates);

			if (normalacyArr.length !== points.length) {
				setRecalculate(true);
			}

			dispatch(
				calcRoute(points, (result) => {
					if (cancel) return;
					if (orderFormTab) {
						orderFormTab.setState((state) => ({
							...state,
							route: result,
						}));
					}
				}),
			);
		} else if (
			orderFormTab &&
			orderFormTab.state?.route !== null &&
			!orderFormTab.form.processedByTaximeter
		) {
			orderFormTab.setState((state) => ({
				...state,
				route: null,
			}));
		}

		if (normalacyArr.length >= 1) {
			// validate again if points changed
			setTabs((prev) =>
				prev.map((tab) => {
					if (tab?.form?.id !== activeTabOrderId) return tab;
					if (tab?.validator?.points) return tab;
					return {
						...tab,
						validator: {
							...tab.validator,
							points: true,
						},
					};
				}),
			);
		}

		return function cleanup() {
			cancel = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [points, activeTabOrderId]);

	// -- end --

	useEffect(() => {
		if (acceptTapOpenModalEvent && firstTabEvent) {
			setPointModal({ visible: true, isInsert: false, isEdit: false });
		}
	}, [acceptTapOpenModalEvent, firstTabEvent, setPointModal]);

	// Retrieve a list of addresses on request
	const debounceRequestAddress = useDebouncedCallback(() => {
		const value = point.value.trim();

		if (value.length >= 3) {
			search(value, {
				country: "ua",
				// country: "md",
				searchType: [],
				near: {
					point: user?.defaultTaxiService?.settlementPoint || null,
					radius: 9000000,
				},
				lang: mapLanguage,
			}).then((response) => {
				const tips = [...response].sort(
					(x, y) => x.distanceMeters - y.distanceMeters,
				);

				if (tips.length >= 1) setAllTips(tips);
				setCityTipsVisible(true);
			});
		} else setAllTips([]);
	}, inputRequestDelayMs);

	/** Calculate order cost request */
	const debounceCalculateCost = useDebouncedCallback(async () => {
		if (recalculate) setRecalculate(false);
		if (isDisabled) return;
		const preparedData = prepareFormData();
		const form = cloneDeep(orderFormTab.form);

		const {
			source,
			orderType,
			counterparty,
			id,
			additionalCost,
			agent,
			paymentChangeType,
			discount,
			phone,
			code,
			paymentType,
			counterRideTotal,
		} = form;
		const { additionalFields } = preparedData.params;
		const { arbitraryCost } = additionalFields;

		if (form.processedByTaximeter) return;

		const orderCostRequest: OrderCostRequest = {
			source,
			paymentType,
			additionalFields: {
				rateSettings: additionalFields?.rateSettings,
				passengersCount: additionalFields?.passengersCount,
				suburbanDistanceOverride:
					additionalFields?.suburbanDistanceOverride,
				idleTimeMilliseconds: additionalFields?.idleTimeMilliseconds,
				waitingTimeMilliseconds:
					additionalFields?.waitingTimeMilliseconds,
				additionalCost: additionalCost || 0,
				taximeter: additionalFields.taximeter || false,
			},
			isPreliminary: preparedData.params.isPreliminary,
			orderDate: preparedData.params.orderDate,

			rateId: preparedData.params.rateId,
			taxiServiceId: preparedData.params.taxiServiceId,
			carClassIds: preparedData.params.carClassIds,
			serviceIds: preparedData.params.serviceIds,
			passengers: preparedData.params.passengers.map((passenger) => ({
				points: passenger.points
					.filter((item) => {
						if (!item) return false;
						if (item.coordinates?.lat === 0) return false;
						if (item.coordinates?.lng === 0) return false;
						return true;
					})
					.sort((a, b) => a.position - b.position),
				location: passenger.location,
				customerId: passenger.customer?.id,
				rideNumber:
					passenger?.customer?.counters?.total ||
					counterRideTotal ||
					1,
			})),
		};
		if (counterparty?.id) {
			orderCostRequest.counterpartyId = counterparty?.id;
		}
		if (counterparty?.checkCardId) {
			orderCostRequest.checkCardId = counterparty?.checkCardId;
		}
		if (id) orderCostRequest.id = id;

		if (discount && discount.active) {
			orderCostRequest.additionalFields.discount = discount;
		}

		if (agent) {
			if (agent.id) {
				orderCostRequest.agentId = agent.id;
			}
			if (agent.reward?.id) {
				orderCostRequest.agentRewardId = agent.reward?.id;
			}
		}

		if (paymentChangeType && phone && code && !counterparty?.checkCardId) {
			orderCostRequest.paymentChangeType = paymentChangeType;
		}

		if (orderType === "wizard" || arbitraryCost) return;
		// Set only when the request really goes: the early returns above used to leave it true and block all later calculations.
		setIsSendCalc(true);
		setLoadingChangeCost(true);
		const costOrder = await getCalcOrderCost(orderCostRequest, {
			dispatcherModal: true,
		});
		if (costOrder) {
			const plan = getDiscountPlans(
				costOrder.settings?.customerDiscountPlans,
			);
			// console.log("[ OrderModal ]: Updated order cost", {
			// 	costOrder,
			// 	plan,
			// });

			calculatedOrderIdsRef.current.add(activeTabOrderId);
			setTabs((tabs) =>
				tabs.map((tab) => {
					if (tab.form.id === activeTabOrderId) {
						const roundTrip = costOrder?.stages?.find(
							(item) => item?.stage === "auto_round_trip",
						);

						const setAutoCalculateRoundTrip = (
							v?: OrderCostStage,
						) => {
							if (!v) return { autoCalculateRoundTrip: false };

							return {
								autoCalculateRoundTrip:
									roundTrip?.details?.auto ?? false,
								...(roundTrip?.details?.isRoundTrip
									? { isIntercityRoadTrip: true }
									: { isIntercityRoadTrip: false }),
							};
						};

						return {
							...tab,
							form: {
								...tab.form,
								price: costOrder.value,
								priceSettings: costOrder?.settings,
								discountCost: costOrder?.discount,
								distance: costOrder?.distances?.total,
								agentRewardAddToPrice: costOrder?.stages?.find(
									(item) => item?.stage === "agent_reward",
								)?.value,
								additionalPlan: plan.additionalPlan,
								mainPlan: plan.mainPlan,
								suburban: costOrder.distances?.suburban,
								companyBillMetadata:
									costOrder?.companyBillMetadata,
								orderSave: false,
								orderPointSave: false,
								...setAutoCalculateRoundTrip(roundTrip),
							},
						};
					}
					return tab;
				}),
			);
		}
		setIsSendCalc(false);
		setLoadingChangeCost(false);
	}, 500);

	// useEffect(() => {
	// 	const {
	// 		rateId,
	// 		taxiServiceId,
	// 		taxiClass = [],
	// 		services = [],
	// 		scheduledTime,
	// 		rateIncludePickupCost,
	// 		isIntercityRoadTrip,
	// 		includePickupMargin,
	// 		enableHourlyService,
	// 		passengersCount,
	// 		points = [],
	// 		suburbanDistanceOverride,
	// 		suburbanDistanceOverrideState,
	// 		suburban,
	// 		waitingTime,
	// 		idleTime,
	// 		paymentType,
	// 		mainPaymentAccount,
	// 		additionalCost,
	// 		counterparty,
	// 		agent,
	// 		discount,
	// 		processedByTaximeter,
	// 	} = orderFormTab?.form || {};

	// 	const value = `${Object.values({
	// 		processedByTaximeter: processedByTaximeter || "",
	// 		activeDiscount: discount?.active || "",
	// 		activeDiscountUsage: discount?.discountUsage?.active || "",
	// 		activeBonusUsage: discount?.bonusUsage?.active || "",
	// 		typeBonusUsage: discount?.bonusUsage?.type || "",
	// 		activeBonusUsageLimit: discount?.bonusUsage?.limit?.active || "",
	// 		maxAmountBonusUsageLimit:
	// 			discount?.bonusUsage?.limit?.maxAmount || "",
	// 		agentId: agent?.id || -1,
	// 		agentRewardId: JSON.stringify(agent?.reward || ""),
	// 		paymentType,
	// 		additionalCost,
	// 		checkCardId: counterparty?.checkCardId || -1,
	// 		waitingTime,
	// 		idleTime,
	// 		suburban,
	// 		suburbanDistanceOverride,
	// 		rateId,
	// 		taxiServiceId,
	// 		scheduledTime: scheduledTime || 0,
	// 		passengersCount,
	// 		suburbanDistanceOverrideState,
	// 		enableHourlyService,
	// 		includePickupMargin,
	// 		rateIncludePickupCost,
	// 		isIntercityRoadTrip,
	// 		taxiClassHash: taxiClass.map((item) => item?.value).join(""),
	// 		services: services.map((item) => item?.value).join(""),
	// 		mainPaymentAccount: JSON.stringify(mainPaymentAccount),
	// 		points:
	// 			points?.map((item) => JSON.stringify(item?.coordinates)) || "",
	// 	}).join("|")}`;

	// 	// eslint-disable-next-line react-hooks/exhaustive-deps
	// }, [
	// 	orderFormTab?.form?.paymentType,
	// 	orderFormTab?.form?.rateId,
	// 	orderFormTab?.form?.taxiServiceId,
	// 	orderFormTab?.form?.taxiClass,
	// 	orderFormTab?.form?.services,
	// 	orderFormTab?.form?.scheduledTime,
	// 	orderFormTab?.form?.rateIncludePickupCost,
	// 	orderFormTab?.form?.includePickupMargin,
	// 	orderFormTab?.form?.enableHourlyService,
	// 	orderFormTab?.form?.suburbanDistanceOverride,
	// 	orderFormTab?.form?.suburbanDistanceOverrideState,
	// 	orderFormTab?.form?.suburban,
	// 	orderFormTab?.form?.waitingTime,
	// 	orderFormTab?.form?.idleTime,
	// 	orderFormTab?.form?.isIntercityRoadTrip,
	// 	orderFormTab?.form?.passengersCount,
	// 	orderFormTab?.form?.points,
	// 	orderFormTab?.form?.mainPaymentAccount,
	// 	orderFormTab?.form?.counterparty?.checkCardId,
	// 	orderFormTab?.form?.additionalCost,
	// 	orderFormTab?.form?.agent?.id,
	// 	orderFormTab?.form?.agent?.reward,
	// 	orderFormTab?.form?.discount?.active,
	// 	orderFormTab?.form?.discount?.discountUsage?.active,
	// 	orderFormTab?.form?.discount?.bonusUsage?.active,
	// 	orderFormTab?.form?.discount?.bonusUsage?.limit?.active,
	// 	orderFormTab?.form?.discount?.bonusUsage?.limit?.maxAmount,
	// 	orderFormTab?.form?.discount?.bonusUsage?.type,
	// 	orderFormTab?.form?.processedByTaximeter,
	// ]);

	const calculationCostTabFormHash = useMemo(() => {
		const {
			rateId,
			taxiServiceId,
			taxiClass = [],
			services = [],
			scheduledTime,
			rateIncludePickupCost,
			isIntercityRoadTrip,
			includePickupMargin,
			enableHourlyService,
			passengersCount,
			points = [],
			suburbanDistanceOverride,
			suburbanDistanceOverrideState,
			suburban,
			waitingTime,
			idleTime,
			paymentType,
			mainPaymentAccount,
			additionalCost,
			counterparty,
			agent,
			discount,
			processedByTaximeter,
		} = orderFormTab?.form || {};
		return `${Object.values({
			customerId: orderFormTab?.state?.customerId || -1,
			processedByTaximeter: processedByTaximeter || "",
			activeDiscount: discount?.active || "",
			activeDiscountUsage: discount?.discountUsage?.active || "",
			activeBonusUsage: discount?.bonusUsage?.active || "",
			typeBonusUsage: discount?.bonusUsage?.type || "",
			activeBonusUsageLimit: discount?.bonusUsage?.limit?.active || "",
			maxAmountBonusUsageLimit:
				discount?.bonusUsage?.limit?.maxAmount || "",
			agentId: agent?.id || -1,
			agentRewardId: JSON.stringify(agent?.reward || ""),
			paymentType,
			additionalCost,
			checkCardId: counterparty?.checkCardId || -1,
			waitingTime,
			idleTime,
			suburban,
			suburbanDistanceOverride,
			rateId,
			taxiServiceId,
			scheduledTime: scheduledTime || 0,
			passengersCount,
			suburbanDistanceOverrideState,
			enableHourlyService,
			includePickupMargin,
			rateIncludePickupCost,
			isIntercityRoadTrip,
			taxiClassHash: taxiClass.map((item) => item?.value).join(""),
			services: services.map((item) => item?.value).join(""),
			mainPaymentAccount: JSON.stringify(mainPaymentAccount),
			points:
				points
					?.filter((item) => {
						if (!item) return false;
						if (item.coordinates?.lat === 0) return false;
						if (item.coordinates?.lng === 0) return false;
						return true;
					})
					.map((item) => JSON.stringify(item?.coordinates)) || "",
		}).join("|")}`;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		orderFormTab?.form?.paymentType,
		orderFormTab?.form?.rateId,
		orderFormTab?.form?.taxiServiceId,
		orderFormTab?.form?.taxiClass,
		orderFormTab?.form?.services,
		orderFormTab?.form?.scheduledTime,
		orderFormTab?.form?.rateIncludePickupCost,
		orderFormTab?.form?.includePickupMargin,
		orderFormTab?.form?.enableHourlyService,
		orderFormTab?.form?.suburbanDistanceOverride,
		orderFormTab?.form?.suburbanDistanceOverrideState,
		orderFormTab?.form?.suburban,
		orderFormTab?.form?.waitingTime,
		orderFormTab?.form?.idleTime,
		orderFormTab?.form?.isIntercityRoadTrip,
		orderFormTab?.form?.passengersCount,
		orderFormTab?.form?.points,
		orderFormTab?.form?.mainPaymentAccount,
		orderFormTab?.form?.counterparty?.checkCardId,
		orderFormTab?.form?.additionalCost,
		orderFormTab?.form?.agent?.id,
		orderFormTab?.form?.agent?.reward,
		orderFormTab?.form?.discount?.active,
		orderFormTab?.form?.discount?.discountUsage?.active,
		orderFormTab?.form?.discount?.bonusUsage?.active,
		orderFormTab?.form?.discount?.bonusUsage?.limit?.active,
		orderFormTab?.form?.discount?.bonusUsage?.limit?.maxAmount,
		orderFormTab?.form?.discount?.bonusUsage?.type,
		orderFormTab?.form?.processedByTaximeter,
		orderFormTab?.state?.customerId,
	]);

	/**
	 * Recalculate the cost when the customer of the open tab changes (address first, phone later):
	 * the customer's discount plans and bonuses change the price.
	 * Opening an order or switching tabs is not a customer change — the pair "tab + customer" is compared.
	 * `recalculate` is reset only when the next calculation starts, so a change during a running one is not lost.
	 * The flag is shared by all tabs, so it is set only when this tab can be calculated right now (same rules as the
	 * calculation effect below); otherwise the next calculation of the tab (e.g. after the address) takes the customer anyway.
	 */
	const customerOfTabRef = useRef<{
		tabUid?: number;
		customerId: number | null;
	}>({ customerId: null });
	const tabUid = orderFormTab?.uid;
	const tabCustomerId = orderFormTab?.state?.customerId ?? null;
	useEffect(() => {
		const previous = customerOfTabRef.current;
		customerOfTabRef.current = { tabUid, customerId: tabCustomerId };

		if (previous.tabUid !== tabUid) return;
		if (previous.customerId === tabCustomerId) return;

		const form = orderFormTab?.form;
		if (!form || form.processedByTaximeter) return;
		const canCalculate =
			form.status === "creating"
				? !!form.points?.length
				: !settings.enableQuickDistribution &&
				  !settings.autosaveOrderSettings.active;
		if (!canCalculate) return;

		setRecalculate(true);
		// Only a customer change should trigger this, the rest is read at that moment.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tabUid, tabCustomerId]);

	useLayoutEffect(() => {
		if (orderFormTab?.form.processedByTaximeter) return;
		if (isSendCalc) return;
		const isSaveCreatingOrder = orderFormTab?.form?.status === "creating";
		const pointsLength = !!orderFormTab?.form?.points?.length;
		const isSaveExistOrder =
			orderFormTab?.form?.status !== "creating" &&
			!settings.enableQuickDistribution &&
			!settings.autosaveOrderSettings.active;

		const list: boolean[] = [
			isSaveCreatingOrder && orderFormTab.form.orderSave && pointsLength,
			isSaveCreatingOrder &&
				orderFormTab.form.orderPointSave &&
				pointsLength,
			isSaveCreatingOrder && recalculate && pointsLength,
		];

		if (list.includes(true)) {
			debounceCalculateCost();
			return;
		}

		const list2: boolean[] = [
			isSaveExistOrder && orderFormTab.form.orderSave,
			isSaveExistOrder && orderFormTab.form.orderPointSave,
			isSaveExistOrder && recalculate,
		];

		if (list2.includes(true)) {
			debounceCalculateCost();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		calculationCostTabFormHash,
		orderFormTab?.form?.status,
		settings.enableQuickDistribution,
		settings.autosaveOrderSettings.active,
		recalculate,
		isSendCalc,
	]);

	// ---- Calculate cost end ----

	// Update points if tab was changed
	useLayoutEffect(() => {
		const comparisonPoint = orderFormTab?.form?.points;

		if (Array.isArray(comparisonPoint)) {
			const oldPoint = JSON.stringify(points);
			const newPoints = JSON.stringify(comparisonPoint);
			if (oldPoint !== newPoints) {
				setPoints(comparisonPoint);
			}
		}
	}, [orderFormTab?.form?.points, points]);

	const hashSegments = useMemo(() => {
		const orderSegments = orderFormTab?.state?.route?.segments;
		const id = orderFormTab?.form?.id;
		if (!id) return "";

		const existHas = segmentsMap.has(id);

		if (!existHas) {
			segmentsMap.set(id, { hash: "", segments: orderSegments || [] });
		}
		if (orderSegments) {
			segmentsMap.set(id, { hash: "", segments: orderSegments || [] });
		}

		const exist = segmentsMap.get(id);
		const hashData = exist?.segments?.map((item) => {
			const first = item?.at(0);
			const last = item?.at(-1);
			return JSON.stringify({ first, last });
		});

		setSegmentsMap((prev) => {
			prev.set(id, {
				hash: hashData?.flat().join("|") || "",
				segments: orderSegments || [],
			});
			return prev;
		});

		if (exist?.hash === "reset") return exist?.hash;
		return hashData?.flat().join("|") || "";
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orderFormTab?.state?.route?.segments, orderFormTab?.form?.id]);

	// const hashTaximeterSegments = useMemo(() => {
	// 	const orderSegments = orderFormTab?.state?.routeTaximeter?.segments;
	// 	const id = orderFormTab?.form?.id;
	// 	if (!id) return "";

	// 	const existHas = segmentsTaximeterMap.has(id);

	// 	if (!existHas) {
	// 		segmentsTaximeterMap.set(id, {
	// 			hash: "",
	// 			segments: orderSegments || [],
	// 		});
	// 	}
	// 	if (orderSegments) {
	// 		segmentsTaximeterMap.set(id, {
	// 			hash: "",
	// 			segments: orderSegments || [],
	// 		});
	// 	}

	// 	const exist = segmentsTaximeterMap.get(id);
	// 	const hashData = exist?.segments?.map((item) => {
	// 		const first = item?.at(0);
	// 		const last = item?.at(-1);
	// 		return JSON.stringify({ first, last });
	// 	});

	// 	setSegmentsTaximeterMap((prev) => {
	// 		prev.set(id, {
	// 			hash: hashData?.flat().join("|") || "",
	// 			segments: orderSegments || [],
	// 		});
	// 		return prev;
	// 	});

	// 	if (exist?.hash === "reset") return exist?.hash;
	// 	return hashData?.flat().join("|") || "";
	// 	// eslint-disable-next-line react-hooks/exhaustive-deps
	// }, [orderFormTab?.state?.routeTaximeter?.segments, orderFormTab?.form?.id]);

	const hashPoints = useMemo(() => {
		const orderPoints = orderFormTab?.form?.points;
		const normalacyArr = [...orderPoints].filter((item) => {
			if (!item) return false;
			return true;
		});

		const id = orderFormTab?.form?.id;

		const hashData = normalacyArr?.map((item) => {
			const dataHash = {
				...item?.coordinates,
				entrance: item?.entrance || "",
				name: item?.name || "",
				title: item?.title || "",
			};
			const payload = JSON.stringify(dataHash);
			return payload;
		});

		if (hashData?.length <= 1 && id) {
			segmentsMap.set(id, { hash: "", segments: [] });
		}

		return hashData?.flat().join("|") || "";
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orderFormTab?.form?.points, orderFormTab?.form?.id]);

	const pointsOrder = useMemo(() => {
		const orderPoints = orderFormTab?.form?.points || [];

		const normalacyArr = [...orderPoints]
			.map((item, index) => ({ ...item, id: index + 1 }))
			.filter((item) => {
				if (!item) return false;
				if (item.showPoint === false && item.isEditRow === true)
					return false;
				return true;
			});
		return normalacyArr || [];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hashPoints, points]);

	// const routeTaximeter = useMemo(() => {
	// 	if (!displayBadges.route) return [];
	// 	return orderFormTab?.state?.routeTaximeter?.segments || [];
	// 	// eslint-disable-next-line react-hooks/exhaustive-deps
	// }, [hashTaximeterSegments, displayBadges.route]);

	const pointsToMap = useMemo(() => {
		if (!displayBadges.route) return [];
		return pointsOrder.filter((item) => {
			if (item.coordinates?.lat === 0) return false;
			if (item.coordinates?.lng === 0) return false;
			return true;
		});
	}, [pointsOrder, displayBadges.route]);

	const route = useMemo(() => {
		if (!displayBadges.route) return [];
		if (pointsToMap.length === 1) return [];
		return orderFormTab?.state?.route?.segments || [];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hashSegments, displayBadges.route, pointsToMap?.length]);

	useLayoutEffect(() => {
		if (modal.data.activeCardId) {
			const exist = modal.data?.cards?.find(
				(card) => card.order?.id === modal.data.activeCardId,
			);
			setOrder(exist || null);
			setActiveOrderId(modal.data.activeCardId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modal.data, setOrder]);

	useEffect(() => {
		if (modal.data.activeCardId) {
			dispatch(
				getOrderHistory(modal.data.activeCardId, (data) => {
					setHistory(data);
				}),
			);
		}
	}, [dispatch, modal.data.activeCardId, setHistory]);

	useEffect(() => {
		if (tabs) {
			dispatch(orderPage.actions.setTabs(tabs));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tabs]);

	// ----- companyBillMetadata start
	const companyBillMetadataHash = useMemo((): string => {
		const currency = orderFormTab.form?.currency?.name || "";
		const phoneType = orderFormTab.form?.phoneType || "";
		const companyBillMetadata = orderFormTab.form?.companyBillMetadata;
		const status = companyBillMetadata?.status || "";
		const orderId = companyBillMetadata?.orderId || "";
		const threshold = companyBillMetadata?.threshold || "";
		const missingAmount = companyBillMetadata?.missingAmount || "";
		const paymentAccountAmount =
			companyBillMetadata?.paymentAccountAmount || "";
		const paymentProcess = companyBillMetadata?.paymentProcess || "";
		const live = companyBillMetadata?.tabs?.live?.amount || "";
		const executable = companyBillMetadata?.tabs?.executable?.amount || "";
		const preliminary =
			companyBillMetadata?.tabs?.preliminary?.amount || "";
		const liveOrders = companyBillMetadata?.tabs?.live?.orders || "";
		const executableOrders =
			companyBillMetadata?.tabs?.executable?.orders || "";
		const preliminaryOrders =
			companyBillMetadata?.tabs?.preliminary?.orders || "";

		return Object.values({
			live,
			executable,
			preliminary,
			liveOrders,
			executableOrders,
			preliminaryOrders,
			currency,
			orderId,
			threshold,
			paymentAccountAmount,
			paymentProcess,
			status,
			missingAmount,
			phoneType,
		}).join("|");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		orderFormTab.form?.companyBillMetadata?.status,
		orderFormTab.form?.companyBillMetadata?.orderId,
		orderFormTab.form?.companyBillMetadata?.threshold,
		orderFormTab.form?.companyBillMetadata?.missingAmount,
		orderFormTab.form?.companyBillMetadata?.tabs?.live?.amount,
		orderFormTab.form?.companyBillMetadata?.tabs?.executable?.amount,
		orderFormTab.form?.companyBillMetadata?.tabs?.preliminary?.amount,
		orderFormTab.form?.companyBillMetadata?.tabs?.live?.orders,
		orderFormTab.form?.companyBillMetadata?.tabs?.executable?.orders,
		orderFormTab.form?.companyBillMetadata?.tabs?.preliminary?.orders,
		orderFormTab.form?.currency?.name,
		orderFormTab.form?.phoneType,
	]);

	const companyBillMetadata =
		useMemo((): BaseCompanyBillMetadata.Value | null => {
			const companyBillMetadata = orderFormTab.form?.companyBillMetadata;
			const status = orderFormTab.form?.companyBillMetadata?.status;
			const currency = orderFormTab.form?.currency;
			const phoneType = orderFormTab.form?.phoneType;
			if (
				companyBillMetadata &&
				status === ThresholdPaymentStatus.THRESHOLD_FAILED
			) {
				setCompanyBillMetadataModal(true);
				return {
					companyBillMetadata,
					currency,
					phoneType,
				};
			}
			setCompanyBillMetadataModal(false);
			return null;
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [companyBillMetadataHash]);

	/** Applies the answer in the dialog to the active tab and recalculates. */
	const applyBalanceAnswer = useCallback(
		(update: Partial<Schema>) => {
			const { form } = orderFormTab;
			const isNew = isNewBalanceOrder(form);
			const next = {
				...form,
				...update,
				// A saved order goes through autosave again, a new one must not save itself.
				orderSave: !isNew,
				orderPointSave: false,
			} as Schema;
			next.balanceAnswerKey = getAutoCreateKey(next);

			skipBalanceTracking(getBalanceHistory(form.id as number), next);
			setTabs((items) =>
				items.map((tab) =>
					tab.form?.id === form.id ? { ...tab, form: next } : tab,
				),
			);
			// The cells keep what was typed: the returned route must show itself.
			if (update.points) setPointsTableKey((key) => key + 1);
			if (next.points?.length) setRecalculate(true);
			setCompanyBillMetadataModal(false);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[orderFormTab.form, getBalanceHistory],
	);

	const companyBillMetadataSave = useCallback(() => {
		applyBalanceAnswer(getYesAnswerForm(orderFormTab.form));
	}, [applyBalanceAnswer, orderFormTab.form]);

	const companyBillMetadataClose = useCallback(() => {
		const { form } = orderFormTab;
		applyBalanceAnswer(
			getNoAnswerForm(form, getBalanceHistory(form.id as number)),
		);
	}, [applyBalanceAnswer, getBalanceHistory, orderFormTab.form]);

	// What changed in the form, and what the last check has passed.
	useEffect(() => {
		const { form } = orderFormTab;
		if (!form?.id || form.id < 0) return;

		const history = getBalanceHistory(form.id);
		trackBalanceChanges(history, form);

		if (calculatedOrderIdsRef.current.delete(form.id)) {
			if (
				form.companyBillMetadata?.status !==
				ThresholdPaymentStatus.THRESHOLD_FAILED
			) {
				rememberBalanceSuccess(history, form);
			}
		}
	}, [orderFormTab.form, getBalanceHistory]);

	useEffect(() => {
		if (!modal.open) balanceHistoryRef.current.clear();
	}, [modal.open]);

	// A saved order does not know its phone type, and without it «Yes» would
	// take off the counterparty of an employee: ask once, the form stays as is.
	const balancePhone = orderFormTab.form?.phone;
	const balanceKnownPhoneType = orderFormTab.form?.phoneType;
	const balanceCompanyId = orderFormTab.form?.companyID;
	useEffect(() => {
		if (!companyBillMetadataModal) return;
		if (!balancePhone || balanceKnownPhoneType) return;
		if (balancePhoneType?.phone === balancePhone) return;

		setBalancePhoneType({ phone: balancePhone, loaded: false });
		dispatch(
			getPhoneInfo(
				{ phone: balancePhone, companyId: balanceCompanyId },
				(res) => {
					setBalancePhoneType({
						phone: balancePhone,
						type: res?.client?.type,
						// No answer: cash stays closed, «No» works.
						loaded: Boolean(res),
					});
				},
			) as any,
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyBillMetadataModal, balancePhone, balanceKnownPhoneType]);

	const companyBillMetadataValue = useMemo(() => {
		if (!companyBillMetadata) return null;
		const known = companyBillMetadata.phoneType;
		const asked =
			balancePhoneType?.phone === balancePhone ? balancePhoneType : null;

		return {
			...companyBillMetadata,
			phoneType: known ?? asked?.type,
			saveLocked: !known && Boolean(balancePhone) && !asked?.loaded,
		};
	}, [companyBillMetadata, balancePhoneType, balancePhone]);
	// ----- companyBillMetadata end

	// TODO: Should be update by id
	const onOrderFormChange = useCallback(
		(form) => {
			setTabs((tabs) =>
				tabs.map((tab) => {
					if (tab?.form?.id !== activeTabOrderId) return tab;

					// if (tab?.form?.id) {
					// 	const exist = formMap.get(tab.form.id);

					// 	console.log(
					// 		"createOrderFormBy -> onOrderFormChange",
					// 		isEqual(exist, form),
					// 	);
					// }

					return { ...tab, form };
				}),
			);
		},
		[activeTabOrderId],
	);
	const onOrderFormInsertPoint = useCallback(() => {
		setPointModal({ visible: true, isInsert: true, isEdit: false });
	}, [setPointModal]);

	const onOrderFormOpenPointModal = useCallback(() => {
		setPointModal({ visible: true, isInsert: false, isEdit: false });
	}, [setPointModal]);

	const onOrderFormEditPoint = useCallback(() => {
		setPointModal({ visible: true, isInsert: false, isEdit: true });
	}, [setPointModal]);

	// ---- Work with modal column widths
	const { widths, setWidths } = useWidths("orders.orderModal", "orderModal");

	const onColumnWidthChange = useCallback(
		(key: ColumnId<"orderModal">, width: number) => {
			if (widths) setWidths({ ...widths, [key]: width });
		},
		[widths, setWidths],
	);

	const onColumnResize = useCallback(
		(width, columnKey) => onColumnWidthChange(columnKey, width),
		[onColumnWidthChange],
	);

	const translatedDefaultColumns = useMemo<
		LegacyTableColumn<string>[]
	>(() => {
		const columns = tables.orderArchiveModal?.columns?.map((column) => ({
			label: t(column?.label),
			key: column?.id,
			resizable: true,
			width: widths[column?.id],
			accessor: column?.id,
			isChecked: true,
		}));

		return columns;
	}, [widths, t]);
	// ------

	/** Displaying calls in the order tab */
	const orderTabsData = useMemo(
		() =>
			tabs.map((tab) => {
				const newTab = cloneDeep(tab);

				const isOwn = newTab?.form?.isOwn;

				const phoneIsOwn = newTab.form?.phone;
				const phoneIsNotOwn =
					newTab.form?.transfer?.lastReceivedOrder?.phones?.sort(
						(a, b) => a.group - b.group,
					)?.[0]?.number;

				const hasExecutor = newTab.form?.transfer?.executor;

				const maskPhoneNumber = (
					phoneNumber?: string,
				): string | undefined => {
					if (
						!phoneNumber ||
						typeof phoneNumber !== "string" ||
						phoneNumber.length < 4
					) {
						return phoneNumber;
					}

					return `${phoneNumber.slice(0, -3)}***`;
				};

				const securePhoneForIsNotOwn = hasExecutor
					? phoneIsNotOwn
					: maskPhoneNumber(phoneIsNotOwn);

				const resultPhoneHeader = isOwn
					? phoneIsOwn
					: securePhoneForIsNotOwn;

				const parkLine =
					newTab?.form?.park_line ||
					modal?.data?.cards?.find((item) => item?.id === newTab?.id)
						?.order?.park_line ||
					"";

				newTab.data.label = (
					<ItemTabHeader
						phone={resultPhoneHeader}
						parkLine={parkLine}
					/>
				) as unknown as string;

				return newTab.data;
			}),
		[tabs, modal.data?.cards],
	);
	// ------

	const orderModalSetAllowBounding = useCallback(() => {
		setPermissionBounds(false);
	}, []);

	const nextElementById = useCallback(
		(elemId: string): HTMLElement | null => {
			const elem = document.getElementById(elemId);
			if (elem) elem.focus();
			return elem;
		},
		[],
	);

	const options = useMemo(
		() => OPTIONS.map((o) => ({ ...o, label: t(o.label) })),
		[OPTIONS, t],
	);

	const isTaximeter = useMemo(
		() => orderFormTab?.form?.processedByTaximeter,
		[orderFormTab?.form?.processedByTaximeter],
	);

	const rightTabs = useMemo(() => {
		try {
			switch (selectedRightTab) {
				// eslint-disable-next-line default-case-last
				default:
				case "map":
					return (
						<AccessGuard
							accessName={TabAccessNames[TabKeys.MAP]}
							fallbackWrapper
						>
							<MapController
								point={point}
								disabled={isDisabled}
								activeCard={activeCard}
								setPoint={setPoint}
								setAllowMapCentralization={
									setAllowMapCentralization
								}
								handleAddPoint={handleAddPoint}
								debounceRequestAddress={debounceRequestAddress}
								selectRevealTip={selectRevealTip}
								mapRevealTipsState={mapRevealTipsState}
								cityTipsVisible={cityTipsVisible}
								allTips={allTips}
								allowMapFocusing={allowMapFocusing}
								setPermissionBounds={setPermissionBounds}
								displayBadges={displayBadges}
								setDisplayBadges={setDisplayBadges}
								trackValue={showTrack}
								trackDisabled={
									!trackOrderId || !trackExecutorId
								}
								onTrackChange={setShowTrack}
							>
								<OrderMap
									key={`${centroid}`}
									readOnly={isDisabled}
									geometry={point.geometry}
									searchType={point.type}
									points={pointsToMap}
									activePoint={activePoint}
									allowMapCentralization={
										allowMapCentralization
									}
									setAllowMapCentralization={
										setAllowMapCentralization
									}
									allowBound={displayBadges.allowBound}
									distance={distance}
									polygons={polygons}
									polygonsVisible={true}
									isSetBounding={allowMapFocusing}
									setAllowBounding={
										orderModalSetAllowBounding
									}
									setPoint={handleChangePoint}
									setCenter={setCenter}
									centroid={centroid}
									route={route}
									isTaximeter={isTaximeter}
									// routeTaximeter={routeTaximeter}
									cars={[]}
									setLoadingChangePoint={
										setLoadingChangePoint
									}
									setLoadingChangeCost={setLoadingChangeCost}
									taxiServiceId={
										orderFormTab.form.taxiServiceId
									}
								>
									{displayBadges.priceZones && (
										<PriceZones
											taxiServiceId={
												orderFormTab.form.taxiServiceId
											}
										/>
									)}
									{displayBadges.sectors && (
										<Sectors
											taxiServiceId={
												orderFormTab.form.taxiServiceId
											}
										/>
									)}
									{displayBadges.parking && (
										<Parkings
											taxiServiceId={
												orderFormTab.form.taxiServiceId
											}
										/>
									)}
									{displayBadges.cars && (
										<MapExecutorLocations
											getLocations={getExecutorLocations}
										/>
									)}
									{showTrack &&
										trackOrderId &&
										trackExecutorId && (
											<ExecutorTrack
												orderId={trackOrderId}
												executorId={trackExecutorId}
												closedAt={
													activeCard?.order?.closedAt
												}
											/>
										)}
								</OrderMap>
							</MapController>
						</AccessGuard>
					);

				case "events":
					return (
						<AccessGuard
							accessName={TabAccessNames[TabKeys.EVENTS]}
							fallbackWrapper
						>
							<InfoModalHistory history={history} />
						</AccessGuard>
					);
				case "conversations":
					// Rendered next to the other tabs, see `callsTabVisited`.
					return null;
				case "files":
					return <FilesWidget form={orderFormTab} validation={{}} />;
			}
		} catch {
			return <div></div>;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		selectedRightTab,
		history,
		route,
		// routeTaximeter,
		activeCard,
		handleAddPoint,
		allowMapFocusing,
		mapRevealTipsState,
		cityTipsVisible,
		point,
		allTips,
		activePoint,
		allowMapCentralization,
		orderFormTab,
		activeTabOrderId,
		polygons,
		handleChangePoint,
		centroid,
		distance,
		setActivePoint,
		selectRevealTip,
		debounceRequestAddress,
		dispatch,
		displayBadges,
		pointsToMap,
		isDisabled,
		getExecutorLocations,
		orderFormTab?.form?.taxiServiceId,
		showTrack,
		trackOrderId,
		trackExecutorId,
	]);

	if (isNotViewBecauseOfAccess) return null;

	return (
		<Modal dimmed useFocusTrap={false}>
			<KeyBind
				keys={["Escape"]}
				onUse={() => {
					handleCloseActiveCard();
				}}
			/>
			<KeyBind settingId="callUpSoftphone" onUse={() => callUp(true)} />
			<KeyBind
				settingId="callDownSoftphone"
				onUse={() => getAndCallDown()}
			/>
			<OrderModalProvider
				tab={orderFormTab}
				ensureBalance={ensureBalance}
				onOrderCreated={onOrderCreated}
				loadingChangeCost={loadingChangeCost}
				costPreviewRunning={isSendCalc}
			>
				<StyledWrap>
					<StyledWrapModal>
						<AccessGuard
							accessName={TabAccessNames[TabKeys.ADD_NEW_ORDER]}
						>
							<OrderTabs.Tabs
								numberIcon={false}
								dragIcon={false}
								activeTab={activeTabIndex}
								slidersData={orderTabsData}
								onTabChange={handleChangeTab}
								onAddTabs={handleAddTab}
								onCloseTab={handleCloseTab}
							/>
						</AccessGuard>

						<ModalController />

						<OrderForm
							tab={orderFormTab}
							disabled={isDisabled}
							setTabAccept={() => setTabAccept(false)}
							onFormChange={onOrderFormChange}
							onOpenPointModal={onOrderFormOpenPointModal}
							onInsert={onOrderFormInsertPoint}
							pointsTableKey={pointsTableKey}
							onEditPoint={onOrderFormEditPoint}
							onChangeActivePoint={setActivePoint}
							tableData={pointsOrder}
							activePoint={activePoint}
							onRowClick={setActivePoint}
							onColumnResize={onColumnResize}
							tableSchema={translatedDefaultColumns}
							tableRowHeight={28}
							setPermissionBounds={setPermissionBounds}
							onSavePoints={onSavePoints}
							loadingChangePoint={loadingChangePoint}
							setLoadingChangePoint={setLoadingChangeCost}
							rightPart={
								<StyledWrapRight sizes="35px 1fr">
									<TabsWrap
										value={selectedRightTab}
										onChange={(value) => {
											setSelectedRightTab(value);
											if (value === "conversations")
												setCallsTabVisited(true);
										}}
										options={options}
										variant="bookmarks"
									/>
									<StyledMapContainer>
										<Suspense fallback={<SuspenseLoader />}>
											{rightTabs}
										</Suspense>
										{(callsTabVisited ||
											selectedRightTab ===
												"conversations") && (
											<div
												style={{
													display:
														selectedRightTab ===
														"conversations"
															? "contents"
															: "none",
												}}
											>
												<AccessGuard
													accessName={
														TabAccessNames[
															TabKeys
																.CONVERSATIONS
														]
													}
													fallbackWrapper
												>
													<OrderCalls
														orderId={
															modal.data
																.activeCardId
														}
													/>
												</AccessGuard>
											</div>
										)}
									</StyledMapContainer>
								</StyledWrapRight>
							}
							footer={
								<FooterMemo
									locked={activeCard?.lockedBy}
									paymentStatusOrder={activeCard?.order}
									save={handleSubmit}
									cancel={handleCloseActiveCard}
									close={handleCloseModal}
									onCloseTab={handleCloseOrderAndTab}
									disabled={isDisabled}
									isLoadingBtnSubmit={
										loadingBtnSubmit || loadingChangePoint
									}
								/>
							}
						/>
					</StyledWrapModal>
				</StyledWrap>

				{companyBillMetadataModal && (
					<CompanyBillMetadata
						value={companyBillMetadataValue}
						onClose={companyBillMetadataClose}
						onSave={companyBillMetadataSave}
					/>
				)}

				{pointModal.visible && (
					<PointModal
						taxiServiceId={orderFormTab.form.taxiServiceId}
						defaultCity={
							orderFormTab?.state?.defaultCity ||
							user?.defaultTaxiService
						}
						getExecutorLocations={getExecutorLocations}
						customerId={orderFormTab?.state?.customerId}
						editPoint={pointModal.isEdit && points[activePoint]}
						isFirstPoint={points.length === 0}
						onClose={handleClosePointModal}
						onSubmit={(point) => {
							const activeTabData = cloneDeep(orderFormTab.form);

							if (pointModal.isEdit) {
								activeTabData.points[activePoint] = point;
								setActivePoint(activePoint);
							} else if (pointModal.isInsert) {
								const pointLength = activeTabData.points.length;
								let activeInsert = activePoint + 1;

								if (pointLength <= 1) {
									nextElementById(`2street`);
									if (pointLength === 1) activeInsert = 1;
									else activeInsert = 0;
								} else if (pointLength > 1) {
									nextElementById(`driver-node-id`);
									if (activeInsert > pointLength) {
										activeInsert = pointLength;
									}
								}

								if (activePoint < 0) activeInsert = 0;
								else if (activePoint === 1) {
									nextElementById(`driver-node-id`);
								}

								if (pointLength === activeInsert) {
									activeTabData.points.splice(
										activeInsert,
										0,
										point,
									);
									if (activeInsert === 0) setActivePoint(1);
									else setActivePoint(activeInsert);
								} else {
									activeTabData.points.splice(
										activeInsert,
										0,
										point,
									);

									setActivePoint(activeInsert);
								}
							} else {
								activeTabData.points.push(point);
								const activeAdd = activeTabData.points.length;
								setActivePoint(activeAdd);
								if (activeAdd === 1) nextElementById(`2street`);
								if (activeAdd > 1) {
									setActivePoint(activeAdd - 1);
									nextElementById(`driver-node-id`);
								}
							}

							setTabs((tabs) =>
								tabs.map((tab) => {
									if (tab.form.id === activeTabOrderId) {
										return {
											...tab,
											form: {
												...activeTabData,
												...dropTaximeterOnPointChange(
													activeTabData,
												),
											},
										};
									}
									return tab;
								}),
							);

							setMapRevealTipsState({
								visible: false,
								data: [],
								activeIndex: -1,
							});

							setPermissionBounds(true);
							onSavePoints();
						}}
					/>
				)}
			</OrderModalProvider>
		</Modal>
	);
};

export default memo(BaseOrderModal);
